import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { usePulseBlock } from '../../hooks/usePulseBlock';
import { isPulseBlockError, isPulseBlockPending } from '../../utils';

export type CapturedBlock = {
  pulseName: string;
  pulseDisplayName?: string | null;
  blockName: string;
  blockDisplayName: string;
};

export type PulseCaptureState = 'added' | 'adding' | 'failed' | 'persisted';

export type PulseCaptureResult = {
  captures: CapturedBlock[];
  eventName?: string;
  state: 'failed' | 'pending' | 'successful';
};

type CapturedBlockState = 'completed' | 'failed' | 'pending';

type PulseBlockCaptureContextValue = {
  captures: CapturedBlock[];
  addCapture: (capture: CapturedBlock) => void;
  observeCaptureResult: (result: PulseCaptureResult) => void;
  captureStateByPulseName: ReadonlyMap<string, PulseCaptureState>;
  projectName?: string;
};

const EMPTY_CAPTURES: CapturedBlock[] = [];
const EMPTY_CAPTURE_STATES = new Map<string, PulseCaptureState>();

const PulseBlockCaptureContext =
  createContext<PulseBlockCaptureContextValue | null>(null);

const PulseBlockCaptureTracker = ({
  capture,
  onStateChange,
}: {
  capture: CapturedBlock;
  onStateChange: (blockName: string, state: CapturedBlockState) => void;
}) => {
  const { pulseBlock, error, isLoading, isPollingTimedOut } = usePulseBlock({
    name: capture.blockName,
  });
  let state: CapturedBlockState = 'completed';

  if (
    error ||
    isPollingTimedOut ||
    (pulseBlock && isPulseBlockError(pulseBlock))
  ) {
    state = 'failed';
  } else if (isLoading || !pulseBlock || isPulseBlockPending(pulseBlock)) {
    state = 'pending';
  }

  useEffect(() => {
    onStateChange(capture.blockName, state);
  }, [capture.blockName, onStateChange, state]);

  return null;
};

export const PulseBlockCaptureProvider = ({
  children,
  projectName,
}: {
  children: ReactNode;
  projectName?: string;
}) => {
  const [captures, setCaptures] = useState<CapturedBlock[]>([]);
  const [capturedBlockStates, setCapturedBlockStates] = useState<
    Record<string, CapturedBlockState>
  >({});
  const pendingCaptureEventNames = useRef(new Set<string>());

  const addCapture = useCallback((capture: CapturedBlock) => {
    setCaptures(current => {
      if (current.some(c => c.blockName === capture.blockName)) return current;
      return [...current, capture];
    });
  }, []);

  const observeCaptureResult = useCallback((result: PulseCaptureResult) => {
    if (!result.eventName) return;

    if (result.state === 'pending') {
      pendingCaptureEventNames.current.add(result.eventName);
      return;
    }

    const wasPending = pendingCaptureEventNames.current.delete(
      result.eventName,
    );
    if (result.state !== 'successful' || !wasPending) return;

    setCaptures(current => {
      const existingBlockNames = new Set(current.map(c => c.blockName));
      const newCaptures = result.captures.filter(capture => {
        if (existingBlockNames.has(capture.blockName)) return false;
        existingBlockNames.add(capture.blockName);
        return true;
      });
      return newCaptures.length > 0 ? [...current, ...newCaptures] : current;
    });
  }, []);

  const updateCapturedBlockState = useCallback(
    (blockName: string, state: CapturedBlockState) => {
      setCapturedBlockStates(current =>
        current[blockName] === state
          ? current
          : { ...current, [blockName]: state },
      );
    },
    [],
  );

  const captureStateByPulseName = useMemo(() => {
    const states = new Map<string, PulseCaptureState>();

    captures.forEach(capture => {
      const blockState = capturedBlockStates[capture.blockName] ?? 'pending';
      const pulseState = states.get(capture.pulseName);

      if (blockState === 'pending') {
        states.set(capture.pulseName, 'adding');
      } else if (blockState === 'failed' && pulseState !== 'adding') {
        states.set(capture.pulseName, 'failed');
      } else if (!pulseState) {
        states.set(capture.pulseName, 'added');
      }
    });

    return states;
  }, [capturedBlockStates, captures]);

  const value = useMemo<PulseBlockCaptureContextValue>(
    () => ({
      captures,
      addCapture,
      observeCaptureResult,
      captureStateByPulseName,
      projectName,
    }),
    [
      captures,
      addCapture,
      observeCaptureResult,
      captureStateByPulseName,
      projectName,
    ],
  );

  return (
    <PulseBlockCaptureContext.Provider value={value}>
      {children}
      {captures.map(capture => {
        const state = capturedBlockStates[capture.blockName];
        return !state || state === 'pending' ? (
          <PulseBlockCaptureTracker
            key={capture.blockName}
            capture={capture}
            onStateChange={updateCapturedBlockState}
          />
        ) : null;
      })}
    </PulseBlockCaptureContext.Provider>
  );
};

export const usePulseBlockCaptures = (): PulseBlockCaptureContextValue => {
  const value = useContext(PulseBlockCaptureContext);

  return useMemo(
    () =>
      value ?? {
        captures: EMPTY_CAPTURES,
        addCapture: () => {},
        observeCaptureResult: () => {},
        captureStateByPulseName: EMPTY_CAPTURE_STATES,
      },
    [value],
  );
};

export const usePulseCaptureState = (pulseName: string): PulseCaptureState => {
  const value = useContext(PulseBlockCaptureContext);
  return value?.captureStateByPulseName.get(pulseName) ?? 'persisted';
};
